import { MenuItem, Modal, Typography, Zoom } from "@mui/material";
import { uniq } from "lodash";
import { memo, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, FlexColBox, Icon } from "../../common/components";
import { createStyles, palette, spacing } from "../../styles/theme";
import {
  DesignFamily,
  DesignProduct,
  ProductFamily,
  ProductLineType,
} from "../../types";
import {
  ProductFamilyList,
  ProductLinesToColor,
  ProductLinesToIcon,
  ProductList,
} from "../../constant/common.constant";
import {
  PRODUCT_CARD_MIN_WIDTH,
  ProductFamilyModalItem,
} from "./components/ProductFamilyModalItem";
import { ProductModalItem } from "./components/ProductModalItem";
import { Select } from "../../common/components/Select/Select";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import ProductLineFilterChip from "./components/ProductLineFilterChip";
import { useAuth } from "../../common/hooks/useAuth";

const styles = createStyles({
  container: {
    p: { xs: spacing.wrapperMobile, lg: spacing.largeDesktop },
    pb: { xs: spacing.xlMobile, lg: spacing.largeDesktop },
    background: palette.white,
    minHeight: "100vh",
    maxHeight: "100vh",
    overflowY: "auto",
    gap: { xs: spacing.regularMobile, lg: spacing.regularDesktop },
  },
  productListContainer: {
    display: "grid",
    gridTemplateColumns: {
      xs: `repeat(auto-fit, minmax(${PRODUCT_CARD_MIN_WIDTH}, 1fr))`,
      lg: `repeat(auto-fit, minmax(${PRODUCT_CARD_MIN_WIDTH}, 1fr))`,
    },
    gap: { xs: spacing.smallMobile, lg: spacing.smallDesktop },
    justifyContent: { xs: "center", lg: "flex-start" },
  },
});

const FilterList = [
  {
    id: ProductLineType.HYPER_CUSTOM,
    color: ProductLinesToColor[ProductLineType.HYPER_CUSTOM],
    labelKey: `line.${ProductLineType.HYPER_CUSTOM}.title`,
    icon: ProductLinesToIcon[ProductLineType.HYPER_CUSTOM],
  },
  {
    id: ProductLineType.ECO,
    color: ProductLinesToColor[ProductLineType.ECO],
    labelKey: `line.${ProductLineType.ECO}.title`,
    icon: ProductLinesToIcon[ProductLineType.ECO],
    adminOnly: true,
  },
  {
    id: ProductLineType.CLASSIC,
    color: ProductLinesToColor[ProductLineType.CLASSIC],
    labelKey: `line.${ProductLineType.CLASSIC}.title`,
    icon: ProductLinesToIcon[ProductLineType.CLASSIC],
    adminOnly: true,
  },
];

interface Props {
  open: boolean;
  onCloseModal: () => void;
  createDesign: (
    family: DesignFamily,
    product: DesignProduct,
    labelTag: string
  ) => void;
}

const ProductModal = ({ createDesign, onCloseModal, open }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useThemeBreakpoints();

  const [filterBy, setFilterBy] = useState<ProductLineType>();
  const [selectedFamily, setSelectedFamily] = useState<ProductFamily>();
  const { isAdmin } = useAuth();

  const onFilterChange = (lineType: ProductLineType) => {
    setFilterBy((prev) => (prev === lineType ? undefined : lineType));
  };

  const filteredProductList = useMemo(
    () =>
      ProductList.filter(
        (p) =>
          (filterBy ? p.line === filterBy : true) &&
          (selectedFamily ? p.family.name === selectedFamily.id : true) &&
          (p.adminOnly ? isAdmin : true)
      ),
    [filterBy, selectedFamily, isAdmin]
  );

  const availableLine = useMemo(() => {
    if (!selectedFamily) {
      return [
        ProductLineType.HYPER_CUSTOM,
        ProductLineType.ECO,
        ProductLineType.CLASSIC,
      ];
    }

    return uniq(
      ProductList.filter((p) =>
        selectedFamily ? p.family.name === selectedFamily.id : true
      ).map((p) => p.line)
    );
  }, [selectedFamily]);

  const handleCreateDesign = (
    family: DesignFamily,
    product: DesignProduct,
    labelTag: string
  ) => {
    setFilterBy(undefined);
    setSelectedFamily(undefined);
    createDesign(family, product, labelTag);
    onCloseModal();
  };

  const handleClose = () => {
    setFilterBy(undefined);
    setSelectedFamily(undefined);
    onCloseModal();
  };

  useEffect(() => {
    ProductFamilyList.forEach((family) => {
      const img = new Image();
      img.src = family.imgName;
    });
    filteredProductList.forEach((product) => {
      const img = new Image();
      img.src = product.imgName;
    });
  }, [filteredProductList]);

  const filteredFamilyList = ProductFamilyList.filter((f) =>
    filterBy ? f.lines.some((line) => line.line === filterBy) : true
  );

  const productContainerMaxWidth = useMemo(() => {
    return selectedFamily || filterBy
      ? `calc(${
          (selectedFamily ? filteredProductList : filteredFamilyList).length *
          214
        }px + ${
          ((selectedFamily ? filteredProductList : filteredFamilyList).length -
            1) *
          8
        }px)`
      : "100%";
  }, [filteredProductList, filteredFamilyList, selectedFamily, filterBy]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Zoom in={open} style={{ transitionDelay: open ? "200ms" : "0ms" }}>
        <FlexColBox sx={styles.container}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            fullWidth
          >
            {selectedFamily ? (
              <Box
                display={"flex"}
                alignItems={"center"}
                gap={{ xs: spacing.mediumMobile, lg: spacing.mediumDesktop }}
              >
                <Icon
                  icon="chevron_left"
                  size={28}
                  onClick={() => setSelectedFamily(undefined)}
                />
                <Typography variant="title">
                  {t(selectedFamily.labelTag)}
                </Typography>
              </Box>
            ) : (
              <Typography variant="title">
                {t("modal.select_canvas.title")}
              </Typography>
            )}
            <Icon
              onClick={handleClose}
              icon="close"
              color={palette.grey.nearBlack}
              size={28}
            />
          </Box>
          {isAdmin && (
            <FlexColBox
              gap={{ xs: spacing.smallMobile, lg: spacing.smallDesktop }}
            >
              <Typography
                color={palette.grey.nearBlack}
                variant="textRegularBold"
              >
                {t("general.filterBy")}
              </Typography>

              <Box
                display={"flex"}
                alignItems={"center"}
                gap={"8px"}
                flexWrap={"wrap"}
                width={{ xs: "100%", lg: "auto" }}
              >
                {isMobile ? (
                  <Select
                    value={filterBy ?? ProductLineType.HYPER_CUSTOM}
                    containerSx={{
                      width: 105,
                      maxWidth: 105,
                    }}
                    sx={{ "& .MuiSelect-select": { display: "flex" } }}
                    renderValue={() => (
                      <Box display="flex" alignItems="center" gap={"4px"}>
                        <Typography
                          variant="actionBoldSmallIcon"
                          color={palette.grey.nearBlack}
                        >
                          {t("general.filterBy")}
                        </Typography>
                        {filterBy && (
                          <Icon
                            icon="circle"
                            filled
                            color={palette.blue.secondary}
                            size={4}
                          />
                        )}
                      </Box>
                    )}
                  >
                    {FilterList.filter((line) =>
                      line.adminOnly ? isAdmin : true
                    ).map((line) => {
                      return (
                        <MenuItem key={line.id} value={line.id} sx={{ p: 0 }}>
                          <ProductLineFilterChip
                            key={line.id}
                            {...line}
                            lineType={line.id}
                            onClick={onFilterChange}
                            active={filterBy === line.id}
                            disabled={!availableLine.includes(line.id)}
                            containerSx={{
                              p: "16px",
                              width: "237px",
                              maxWidth: "237px",
                              border: "none",
                            }}
                            iconSize={29}
                            description={t(`line.${line.id}.description`)}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                ) : (
                  FilterList.filter((line) =>
                    line.adminOnly ? isAdmin : true
                  ).map((line) => (
                    <ProductLineFilterChip
                      key={line.id}
                      {...line}
                      lineType={line.id}
                      onClick={onFilterChange}
                      active={filterBy === line.id}
                      disabled={!availableLine.includes(line.id)}
                      containerSx={{
                        p: "16px",
                        width: { xs: "100%", sm: "auto" },
                        maxWidth: { xs: "100%", sm: "fit-content" },
                      }}
                      iconSize={29}
                      description={t(`line.${line.id}.description`)}
                    />
                  ))
                )}
              </Box>
            </FlexColBox>
          )}
          <Box
            sx={{
              ...styles.productListContainer,
              maxWidth: productContainerMaxWidth,
            }}
          >
            {selectedFamily
              ? filteredProductList.map((product) => (
                  <ProductModalItem
                    product={product}
                    onCreateDesign={handleCreateDesign}
                    key={product.product.name}
                    dataTest={`selectCanvas.product.${product.product.name}`}
                    isFiltering={!!filterBy}
                  />
                ))
              : filteredFamilyList.map((family) => (
                  <ProductFamilyModalItem
                    family={family}
                    onFamilySelect={() => setSelectedFamily(family)}
                    key={family.id}
                    dataTest={`selectCanvas.family.${family.id}`}
                  />
                ))}
          </Box>
        </FlexColBox>
      </Zoom>
    </Modal>
  );
};

export default memo(ProductModal);
